/*
 * A conversation's or a thread's timeline: core's windowed list over chat's rows. What is chat's here
 * is how each row describes itself to the list (`rowShape`) and the pill's words — "5 new messages",
 * and "since Friday 17:40" under a *New messages* line, because after a weekend a number is a number
 * and a time is a place to start. Everything about scrolling, paging, the DOM cap, the keyboard and
 * the pill's arithmetic is the list's (core/list/WindowedList.tsx).
 */

import { useCallback, useMemo } from "react"
import { formatDayTime } from "../../../core/i18n/format.ts"
import { useNow } from "../../../core/i18n/useNow.ts"
import type { RowShape, Unseen, WindowedListProps } from "../../../core/list/types.ts"
import { WindowedList } from "../../../core/list/WindowedList.tsx"
import { t } from "../i18n/t.ts"
import "./list.css"
import type { Row } from "./types.ts"

/**
 * What the list needs of one row. Only a message is an item — a stop for the arrow keys, a member of
 * the read range; a gone permalink stands in for the message it names; a send on its way is content
 * but not yet an item. `meId`'s own messages are never new to them.
 */
function rowShape(row: Row, index: number, meId: number): RowShape {
	switch (row.kind) {
		case "message":
			return { key: `m${row.seq}`, kind: row.kind, seq: row.seq, own: row.msg.authorId === meId }
		case "pending":
			return { key: `p${row.clientMsgId}`, kind: row.kind, seq: 0, pending: true }
		case "day":
			return { key: `d${row.dayStartMs}`, kind: row.kind, seq: 0 }
		case "unread":
			return { key: `u${row.seq}`, kind: row.kind, seq: 0 }
		case "historyStart":
			return { key: `h${row.visibleFromSeq}-${index}`, kind: row.kind, seq: 0 }
		case "gone":
			return { key: `g${row.seq}`, kind: row.kind, seq: 0, goneSeq: row.seq }
	}
}

export interface MessageListProps extends Omit<
	WindowedListProps<Row>,
	"shapeOf" | "newLabel" | "loadingOlderLabel"
> {
	/**
	 * The reader's own account. A message they wrote themselves is never one of the "new" ones the
	 * pill counts — it is the one message they know about (X-A1 A9, D16).
	 */
	meId?: number | undefined
}

export function MessageList({ meId = 0, rows, ...list }: MessageListProps) {
	// The clock the pill's "since" stamp is named against. An hour rather than the hook's minute: the
	// only thing that can change in the label is the day it belongs to, and a minute tick through the
	// list would re-render three hundred rows for a stamp nobody is watching.
	const now = useNow(3_600_000)
	const shapeOf = useCallback((row: Row, index: number) => rowShape(row, index, meId), [meId])
	const hasUnreadDivider = useMemo(() => rows.some((row) => row.kind === "unread"), [rows])
	const newLabel = useCallback(
		({ count, first }: Unseen<Row>) =>
			hasUnreadDivider && first?.kind === "message"
				? t("chat.list.newSince", { count, when: formatDayTime(first.msg.createdAt, now) })
				: t("chat.list.jumpToLatestCount", { count }),
		[hasUnreadDivider, now],
	)
	return (
		<WindowedList
			{...list}
			rows={rows}
			shapeOf={shapeOf}
			newLabel={newLabel}
			loadingOlderLabel={t("chat.list.loadingEarlier")}
		/>
	)
}
